import React, { useCallback, useMemo, useState } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { Feather } from '@expo/vector-icons';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Paprika } from '../../styles/theme';
import type { AppDispatch, RootState } from '../../redux/store';
import {
  selectOnboardingPicks,
  selectOnboardingWeekStart,
  selectReadingCardDismissed,
  dismissReadingCard,
  recordAxisFlip,
} from '../../redux/onboardingSlice';
import {
  selectTasteAxes,
  selectAllergens,
  selectCustomAllergens,
  selectMealSchedule,
  selectDefaultFamilySize,
  flipTasteAxis,
} from '../../redux/userPreferencesSlice';
import { selectAllRecipeMetadata } from '../../redux/recipeMetadataSlice';
import { setWeeklyPlan, type PlannedMeal } from '../../redux/mealPlanSlice';
import { buildGroceryList } from '../../redux/groceryListSlice';
import { flipAxis, profileKey, type Axis } from '../../services/tasteAxes';
import { readingParams } from '../../services/onboardingQuiz';
import { buildAllergenTerms } from '../../services/allergenMatcher';
import { buildOnboardingWeek } from '../../services/onboardingWeek';
import { loadPlannerLibrary, ONBOARDING_LIBRARY_WAIT_MS } from '../../services/plannerLibrary';
import { todayIso } from '../../utils/localDate';
import { AxisChip } from './AxisChip';

interface Props {
  /** The week on screen; the card only shows for the onboarding week. */
  weekStart: string;
  /** Meals of that week; cooked rows survive a flip. */
  weekMeals: PlannedMeal[];
}

/**
 * The one-time read-back of the profile on the existing week screen: the
 * picks quoted in Du Chef's words, the « Concrètement » line and the three
 * axis chips. A chip flips its axis and rebuilds the week; the cross removes
 * the card for good.
 *
 * **Desktop only.** On a phone it ate the whole first screen, so `WeeklyMobile`
 * does not mount it — the phone's read-back is the profile beat during
 * onboarding.
 */
export function ReadingCard({ weekStart, weekMeals }: Props) {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const store = useStore<RootState>();
  const axes = useSelector(selectTasteAxes);
  const picks = useSelector(selectOnboardingPicks);
  const dismissed = useSelector(selectReadingCardDismissed);
  const onboardingWeekStart = useSelector(selectOnboardingWeekStart);
  const [line, setLine] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const lang: 'fr' | 'en' = i18n.language?.startsWith('fr') ? 'fr' : 'en';
  const params = useMemo<Record<string, string | number>>(() => ({ ...readingParams(picks, lang) }), [picks, lang]);

  const rebuild = useCallback(async (nextAxes: NonNullable<typeof axes>) => {
    setBusy(true);
    try {
      const state = store.getState();
      const recipes = await loadPlannerLibrary({
        language: lang, uid: state.auth?.user?.uid, indexed: state.recipeIndex?.entries, deadlineMs: ONBOARDING_LIBRARY_WAIT_MS,
      });
      const { meals } = await buildOnboardingWeek({
        weekStart,
        recipes,
        metadata: selectAllRecipeMetadata(state),
        axes: nextAxes,
        allergenTerms: buildAllergenTerms(selectAllergens(state), selectCustomAllergens(state)),
        dislikedIngredients: state.userPreferences?.dislikedIngredients,
        dietaryRestrictions: state.userPreferences?.dietaryRestrictions,
        schedule: selectMealSchedule(state),
        servings: selectDefaultFamilySize(state),
        todayIso: todayIso(),
      });
      const cooked = weekMeals.filter((m) => m.status === 'cooked');
      const cookedDates = new Set(cooked.map((m) => m.scheduledFor));
      const merged = [...cooked, ...meals.filter((m) => !cookedDates.has(m.scheduledFor))];
      dispatch(setWeeklyPlan({ weekStartDate: weekStart, meals: merged, source: 'onboarding_flip' }));
      // Keep the list covering the whole plan, as onboarding built it (#722).
      const lastDinner = merged.reduce<string | null>(
        (latest, m) => (!latest || m.scheduledFor > latest ? m.scheduledFor : latest),
        null,
      );
      dispatch(buildGroceryList({ weekStart, coversUntil: lastDinner }));
    } finally {
      setBusy(false);
    }
  }, [dispatch, store, weekStart, weekMeals]);

  const onFlip = useCallback((axis: Axis) => {
    if (!axes || busy) return;
    const next = flipAxis(axes, axis);
    dispatch(flipTasteAxis(axis));
    dispatch(recordAxisFlip({ axis }));
    setLine(t(`onboarding.reading.corrected.${axis}`));
    void rebuild(next);
  }, [axes, busy, dispatch, rebuild, t]);

  if (!axes || dismissed || picks.length < 3 || !onboardingWeekStart || onboardingWeekStart !== weekStart) return null;

  const paragraph = line ?? [
    t(`onboarding.reading.base.${axes.base}`, params),
    t(`onboarding.reading.time.${axes.time}`, params),
    t(`onboarding.reading.heat.${axes.heat}`),
  ].join(' ');

  return (
    <View style={styles.card} testID="onb-reading-card">
      <View style={styles.top}>
        <Text style={styles.quote}>{paragraph}</Text>
        <Pressable
          onPress={() => dispatch(dismissReadingCard())}
          testID="onb-reading-close"
          accessibilityRole="button"
          accessibilityLabel={t('onboarding.reading.close')}
          hitSlop={8}
          style={styles.close}
        >
          <Feather name="x" size={16} color={Paprika.textMuted} />
        </Pressable>
      </View>
      <View style={styles.inset}>
        <Text style={styles.kicker}>{t('onboarding.reading.concretementKicker')}</Text>
        <Text style={styles.concretement}>{t(`onboarding.reading.concretement.${profileKey(axes)}`)}</Text>
      </View>
      <View style={styles.chips}>
        <AxisChip testID="onb-axis-base" disabled={busy} label={t(`onboarding.reading.chip.base.${axes.base}`)} onPress={() => onFlip('base')} />
        <AxisChip testID="onb-axis-time" disabled={busy} label={t(`onboarding.reading.chip.time.${axes.time}`)} onPress={() => onFlip('time')} />
        <AxisChip testID="onb-axis-heat" disabled={busy} label={t(`onboarding.reading.chip.heat.${axes.heat}`)} onPress={() => onFlip('heat')} />
      </View>
      <Text style={styles.hint}>{t('onboarding.reading.hint')}</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    backgroundColor: Paprika.brandTint,
    borderWidth: 1,
    borderColor: Paprika.brandSoft,
    borderRadius: 16,
    padding: 14,
    gap: 10,
    marginBottom: 14,
  },
  top: { flexDirection: 'row', alignItems: 'flex-start', gap: 8 },
  quote: { flex: 1, fontFamily: 'Newsreader-Italic', fontSize: 15, lineHeight: 20, color: Paprika.textPrimary },
  close: { padding: 2 },
  inset: { backgroundColor: Paprika.cardBg, borderRadius: 10, padding: 10, gap: 2 },
  kicker: { fontFamily: 'Inter-SemiBold', fontSize: 9, letterSpacing: 1, textTransform: 'uppercase', color: Paprika.accent },
  concretement: { fontFamily: 'Inter-Regular', fontSize: 12, lineHeight: 16, color: Paprika.textPrimary },
  chips: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
  hint: { fontFamily: 'Inter-Regular', fontSize: 11, color: Paprika.textMuted },
});
